import { type JSX, useMemo } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import { flip$ } from '@/shared/assets/tokens';
import { Typography } from '@/shared/components/atoms/Typography';
import { FlipLabel } from '@/shared/components/molecules/FlipLabel';
import { Modal, type ModalProps } from '@/shared/components/molecules/Modal';
import { type RedeemedEventFragment } from '@/shared/graphql/generated/explorer/graphql';
import Copy from '@/shared/icons/Copy';
import { etherscanUrl, copy } from '@/shared/utils/helpers';
import TokenAmount from '@/shared/utils/TokenAmount';
import Hero from '../../../assets/svg/empty-redemption.svg';
import { formatDateTime } from '../../../utils/number';

function PreviousRedemptionsEmptyState(): JSX.Element {
  return (
    <div className="flex flex-col text-center">
      <Hero />
      <span className="text-cf-light-1">You haven&apos;t made any redemptions.</span>
    </div>
  );
}

interface PreviousRedemptionsModalProps extends ModalProps {
  redemptions: RedeemedEventFragment[] | undefined;
}

const PreviousRedemptionsModal = ({
  active,
  heading,
  redemptions = [],
  onCancel,
}: PreviousRedemptionsModalProps): JSX.Element | null => {
  const body = useMemo(() => {
    if (redemptions.length === 0) {
      return (
        <div className="flex items-center justify-center py-20">
          <PreviousRedemptionsEmptyState />
        </div>
      );
    }

    return redemptions.map((redemption) => (
      <div
        key={redemption.redeemedTxHash}
        className="flex items-center border-b border-cf-gray-4 py-4 first:pt-0 last:border-0 last:pb-0"
      >
        <div className="mb-2 flex flex-col space-y-2 text-left">
          <FlipLabel amount={new TokenAmount(redemption.amount, flip$.decimals)} large />
          <div className="flex">
            {redemption.account.alias && (
              <>
                <Typography variant="label-small" className="text-cf-light-1">
                  {redemption.account.alias}
                </Typography>
                <div className="mx-1 border-l border-cf-gray-5" />
              </>
            )}
            <Typography variant="label-small" className="text-cf-light-1">
              {abbreviate(redemption.account.idSs58, 5)}
            </Typography>
          </div>

          <Typography variant="label-small" className="text-cf-light-1">
            {formatDateTime(new Date(redemption.event.block.timestamp).getTime())}
          </Typography>
          <div className="flex items-center space-x-2">
            <Typography variant="label-small">
              <a
                className="underline"
                href={etherscanUrl('tx', redemption.redeemedTxHash as string)}
                target="_blank"
                rel="noreferrer"
              >
                {abbreviate(redemption.redeemedTxHash)}
              </a>
            </Typography>
            <button type="button" onClick={() => copy(redemption.redeemedTxHash ?? '')}>
              <Copy />
            </button>
          </div>
        </div>
      </div>
    ));
  }, [redemptions]);

  return (
    <Modal active={active} heading={heading} onCancel={onCancel} maxHeight="570">
      {body}
    </Modal>
  );
};

export default PreviousRedemptionsModal;
